import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ToastProvider } from '@/components/feedback/Toast';

import { CURRENT_USER_KEY } from '@/features/auth/api';
import { markSessionEnded } from '@/features/auth/sessionEnded';

import { createQueryClient } from './queryClient';

/**
 * Everything the tree needs to run.
 *
 * The client is created inside state rather than at module scope so each mount gets
 * its own, which is what keeps one test's cached data out of the next test.
 */
export function Providers({ children, client }: { children: ReactNode; client?: QueryClient }) {
  const [fallback] = useState(() => {
    // A 401 from any request clears the signed-in user, and the route guard reacts by
    // sending the user to the login screen. Done through the cache rather than by
    // navigating from here, so there is one source of truth for "who is signed in".
    const created: QueryClient = createQueryClient(() => {
      // Only a 401 that interrupted a signed-in session is an expiry worth
      // explaining. Two other things reach this handler and are not:
      //
      //   - a failed sign-in, which the API answers 401 by design;
      //   - in principle any 401 raised before anyone signed in.
      //
      // The cached user separates them. It is null on the login screen and an
      // object only once somebody is actually signed in. (The initial
      // GET /auth/me never arrives here at all: useCurrentUser catches its own
      // 401 and resolves null, so that query succeeds rather than erroring.)
      if (created.getQueryData(CURRENT_USER_KEY)) markSessionEnded();
      created.setQueryData(CURRENT_USER_KEY, null);
    });
    return created;
  });

  return (
    <QueryClientProvider client={client ?? fallback}>
      <ToastProvider>{children}</ToastProvider>
    </QueryClientProvider>
  );
}
